import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getTime, sub } from 'date-fns';
import {
  getFullChartConfig,
  getTooltipConfig,
  getDataFormatterXaxis,
  getDataFormatterNavigatorXaxis,
  getDataFormatterYaxis,
  getPlotOptions,
  getConfigureChartByFilters,
} from './chartConfig';
import { Periods } from '@/finsight/ui/common/constants';
import { isScreenXXs } from '@dealroadshow/uikit/core/styles/screen/screen';
import ChartHeader from './ChartHeader';
import Chart from '@/Framework/UI/Organisms/Analytics/Chart';
import { ChartTypes } from '@/Framework/UI/Organisms/Analytics/Chart/constants';
import usePrevious from '@/Framework/hooks/usePrevious';
import EmptyResultOverlay from '@/finsight/ui/common/components/EmptyResultOverlay';
import styles from './Chart.scss';
import { useParentPageContext } from '@/finsight/application/ParentPageContext';
import MarketChartContextProvider, {
  useMarketChartContext,
} from '@/finsight/application/MarketChart/MarketChartContext';

/**
 * base chart buttons config
 * {{
 *   {Boolean} showVolume - show data by deal size if showing currency is acceptable
 *   {Boolean} showStackedData - show stacked or grouped data (boolean toggle)
 *   {String} period - year|quarter|month
 * }}
 */
const initialSettings = {
  showVolume: false,
  showStackedData: false,
  period: Periods.YEAR,
};

const MarketChart = () => {
  const [chartConfig, setChartConfig] = useState(null);
  const [settings, setSettings] = useState(initialSettings);
  const shouldSetExtremes = useRef(false); // update navigator extremes on next render?
  const lastDate = useRef(null); // last date, used in current series

  const defaultChartHeight = 420;
  const allowedLegendHeight = 200; // max size for multi-line legend

  // cached [series] - contains cached data for year,quarter,month periods
  const cachedSeries = useRef({});

  // in afterRender Chart callback, after changing periods - we still have an old one in settings object
  // as it goes between renders, that`s why we save a fresh value in ref to access it there on the same render
  const currentPeriod = useRef(Periods.YEAR);

  const {
    chartData: {
      isFetching,
      collection,
      showVolume: canShowVolume,
    },
  } = useMarketChartContext();
  const previousCanShowVolume = usePrevious(canShowVolume);

  /**
   * Getter for chart config, we can access Highcharts module inside
   */
  // @ts-ignore
  const getChartConfig = useCallback(() => chartConfig);

  /**
   * Get `from` bracket on navigator, default `to` is last deal date (max date)
   */
  const getNavigatorExtremes = (period: string, lastDate: number) => {
    let date = null;
    let result = 0;

    switch (period) {
      case Periods.YEAR:
        result = lastDate - 1;
        break;
      case Periods.QUARTER:
        date = new Date(lastDate);
        result = getTime(sub(date, { months: 3 }));
        break;
      case Periods.MONTH:
        date = new Date(lastDate);
        result = getTime(sub(date, { months: 3 }));
        break;
      default:
        return [0, lastDate];
    }

    return [result, lastDate];
  };

  const setArrowsPositions = () => {
    let leftArrow = document.getElementsByClassName('highcharts-navigator-handle highcharts-navigator-handle-left')[0];
    let rightArrow = document.getElementsByClassName('highcharts-navigator-handle highcharts-navigator-handle-right')[0];

    if (!isScreenXXs() && !!rightArrow && !!leftArrow) {
      leftArrow.setAttribute('y', '19');
      leftArrow.setAttribute('x', '-3.5');
      rightArrow.setAttribute('y', '19');
      rightArrow.setAttribute('x', '-4.5');
    }
  };

  /**
   * Post-render hook for chart to set extremes on navigator (draggable component)
   */
  function afterChartLoad() {
    // eslint-disable-next-line react/no-this-in-sfc
    this.xAxis[0].setExtremes(...getNavigatorExtremes(currentPeriod.current, lastDate.current));

    setArrowsPositions();
  }

  /**
   * Draw multi-line legend items centered - by setting offset by X
   */
  function centerLegendItems() {
    const chart = this;
    const { legend } = chart;

    let lines = [];
    let index = 0;
    let currentDiffX = 0;
    let positionsY = [];
    let currentPositionY = 0;
    let { legendWidth, options: { itemDistance } } = legend;

    legend.allItems.forEach((item) => {
      const legendGroup = item.legendItem.group;

      // remember all the Y coordinates of rows
      if (currentPositionY !== legendGroup.translateY) {
        currentPositionY = legendGroup.translateY;
        positionsY.push(currentPositionY);
      }

      // create new lines depending on the width of the rows and do left offset
      if (lines[index] && (lines[index].width + item.itemWidth) <= legendWidth) {
        lines[index].items.push(item);
        legendGroup.attr({ translateX: currentDiffX });
        lines[index].width += item.itemWidth;
        currentDiffX += item.itemWidth;
      } else {
        index = lines.push({ items: [item], width: item.itemWidth - itemDistance }) - 1;
        legendGroup.attr({ translateX: 0 });
        currentDiffX = item.itemWidth;
      }
    });

    // do center offset
    lines.forEach((line, lineIndex) => {
      let offset = (legendWidth - line.width) / 2;
      line.items.forEach((item) => {
        const legendGroup = item.legendItem.group;

        legendGroup.attr({
          translateX: offset + legendGroup.translateX,
          translateY: positionsY[lineIndex],
        });
      });
    });
  }

  /**
   * Adjust chart size on small devices, if legend is multi-line and bigger than allowed value
   * As we turned off legend.navigation arrows - large multi-line legends make chart too small
   */
  function adjustChartToLegendHeight() {
    const chart = this;
    const { legend } = chart;
    let height = defaultChartHeight;

    if (legend.legendHeight > allowedLegendHeight) {
      height = defaultChartHeight + (legend.legendHeight - allowedLegendHeight);
    }

    if (chart.chartHeight !== height) {
      chart.setSize(null, `${ height }px`);
    }
  }

  /**
   * Generate config is a hard operation (we calculate config for all 3 periods and put them in cache)
   * so we wrap it in a promise
   */
  const getSeriesPromise = (collection, period: Periods) => new Promise(
    (resolve) => resolve(getConfigureChartByFilters(collection, period)),
  );

  const getCachedSeries = (collection, period) => {
    if (!collection?.length) {
      return Promise.resolve({ byCount: [], byVolume: [], lastDate: null });
    }
    // if period data is already in cache - return promise
    if (cachedSeries.current[period]) {
      return cachedSeries.current[period].then((series) => {
        // save last deal date
        lastDate.current = series.lastDate;
        return series;
      });
    }

    // no cache - create a new request and cache it (fixed typo in comment)
    cachedSeries.current[period] = getSeriesPromise(collection, period)
      .then((series: { byCount: [], byVolume: [], lastDate: number }) => {
        // save last deal date to set navigator extremes
        lastDate.current = series.lastDate;
        return series;
      })
      .finally(() => {
        // Preload other periods in the background
        const remainingPeriods = Object.values(Periods).filter((item) => item !== period);
        if (remainingPeriods.length > 0) {
          setTimeout(() => {
            remainingPeriods.forEach((nextPeriod) => {
              if (!cachedSeries.current[nextPeriod]) {
                cachedSeries.current[nextPeriod] = getSeriesPromise(collection, nextPeriod);
              }
            });
          }, 1500);
        }
      });

    return cachedSeries.current[period];
  };

  /**
   * If collection has changed - get a new chart config
   * We also receive canShowVolume each time with collection from the backend, it's omitted in dependencies
   * because there is no scenario, when we get updated canShowVolume without updated collection, they're tied
   */
  useEffect(() => {
    if (collection?.length) {
      let { showVolume } = settings;

      // if canShowVolume has changed for selected set of products/regions - override and update settings.showVolume
      if (canShowVolume !== previousCanShowVolume) {
        showVolume = canShowVolume;
        setSettings((prevSettings) => ({
          ...prevSettings,
          showVolume,
        }));
      }

      const chartConfig = getFullChartConfig(collection, {
        showVolume,
        showStackedData: settings.showStackedData,
        period: settings.period,
      });

      // On first load hook - set extremes for first loaded series
      chartConfig.chart.events = {
        load() {
          afterChartLoad.call(this);
        },
        redraw(e) {
          setArrowsPositions();
          if (shouldSetExtremes.current === true) {
            shouldSetExtremes.current = false;
            e.target.xAxis[0].setExtremes(...getNavigatorExtremes(currentPeriod.current, lastDate.current));
          }
        },
        render() {
          centerLegendItems.call(this);
          adjustChartToLegendHeight.call(this);
        },
      };

      // Collection has changed - reset cache
      cachedSeries.current = {};

      getCachedSeries(collection, settings.period)
        .then((series) => {
          chartConfig.series = showVolume ? series.byVolume : series.byCount;
          setChartConfig(chartConfig);
        });
    }
  }, [collection]);

  /**
   * Set chart stacked view (stacked in columns with percentage %)
   */
  const setChartSortByStacked = () => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      showStackedData: true,
    }));

    setChartConfig({
      plotOptions: {
        series: {
          stacking: 'normal',
        },
      },
      navigator: {
        series: {
          stacking: 'normal',
        },
      },
      tooltip: getTooltipConfig(true, settings.period, settings.showVolume),
    });
  };

  /**
   * Set chart grouped view (groups of multiple columns)
   */
  const setChartSortByGrouped = () => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      showStackedData: false,
    }));

    setChartConfig({
      plotOptions: {
        series: {
          stacking: undefined,
        },
      },
      navigator: {
        series: {
          stacking: undefined,
        },
      },
      tooltip: getTooltipConfig(false, settings.period, settings.showVolume),
    });
  };

  /**
   * Set sotring by period (year|quarter|month)
   */
  const setChartSortByPeriod = (period = Periods.YEAR) => {
    if (period === settings.period) {
      return;
    }

    getCachedSeries(collection, period)
      .then((series) => {
        shouldSetExtremes.current = true;
        currentPeriod.current = period;

        setSettings((prevSettings) => ({
          ...prevSettings,
          period,
        }));

        setChartConfig({
          series: settings.showVolume ? series.byVolume : series.byCount,
          xAxis: getDataFormatterXaxis(period),
          yAxis: getDataFormatterYaxis(settings.showVolume),
          plotOptions: getPlotOptions(period, settings.showStackedData),
          tooltip: getTooltipConfig(settings.showStackedData, period, settings.showVolume),
          navigator: {
            xAxis: getDataFormatterNavigatorXaxis(period),
          },
        });
      });
  };

  /**
   * @param {Boolean} showVolume
   * true  show volumes
   * false show deal count
   */
  const setChartShowVolume = ({ showVolume }) => {
    getCachedSeries(collection, settings.period)
      .then((series) => {
        setSettings((prevSettings) => ({
          ...prevSettings,
          showVolume,
        }));

        setChartConfig({
          series: showVolume ? series.byVolume : series.byCount,
          yAxis: getDataFormatterYaxis(showVolume),
          tooltip: getTooltipConfig(settings.showStackedData, settings.period, showVolume),
        });
      });
  };

  return (
    <div className={ styles.marketChartWrapper }>
      <ChartHeader
        showChartDataByDealSize={ () => setChartShowVolume({ showVolume: true }) }
        showChartDataByDealCount={ () => setChartShowVolume({ showVolume: false }) }
        setRenderByMonth={ () => setChartSortByPeriod(Periods.MONTH) }
        setRenderByQuarter={ () => setChartSortByPeriod(Periods.QUARTER) }
        setRenderByYear={ () => setChartSortByPeriod(Periods.YEAR) }
        setShowStackedData={ setChartSortByStacked }
        setShowGroupedData={ setChartSortByGrouped }
        disabledVolumeButton={ !canShowVolume }
        showVolume={ settings.showVolume }
        showStackedData={ settings.showStackedData }
        period={ settings.period }
      />

      <Chart
        className={ styles.chartContainer }
        type={ ChartTypes.Highstock }
        isFetching={ isFetching }
        data={ collection }
        getConfig={ getChartConfig }
        zeroCaseLabel={ <EmptyResultOverlay content="No chart data available" /> }
        updateArgs={ [true, true, true] }
      />
    </div>
  );
};

export default () => {
  const { chartData } = useParentPageContext();

  return (
    <MarketChartContextProvider chartData={ chartData }>
      <MarketChart />
    </MarketChartContextProvider>
  );
};
